import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { FiDownload, FiX } from 'react-icons/fi';
import {
  exportWhatsAppGroupParticipants,
  getWhatsAppGroups,
  WhatsAppGroupDto,
} from '../../services/SectorService';
import {
  resolveGroupParticipantsErrorMessage,
  resolveWhatsAppGroupApiId,
} from '../../utils/broadcastContactUtils';
import { LoadingSpinner } from '../LoadingSpinner';

const MAX_GROUPS = 50;

export interface ExportGroupContactsModalProps {
  open: boolean;
  sectorId: number;
  onClose: () => void;
  onExported?: (info: { contactCount: number; groupsProcessed: number; groupsFailed: number }) => void;
}

export const ExportGroupContactsModal: React.FC<ExportGroupContactsModalProps> = ({
  open,
  sectorId,
  onClose,
  onExported,
}) => {
  const [groups, setGroups] = useState<WhatsAppGroupDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const loadGroups = useCallback(async () => {
    if (sectorId <= 0) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getWhatsAppGroups(sectorId);
      setGroups(Array.isArray(res?.data) ? res.data : []);
    } catch (err: unknown) {
      setError(resolveGroupParticipantsErrorMessage(err, 'Não foi possível carregar os grupos'));
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [sectorId]);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setSearch('');
    setError(null);
    void loadGroups();
  }, [open, loadGroups]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => (g.name || '').toLowerCase().includes(q));
  }, [groups, search]);

  const toggle = (listId: number) => {
    if (!selected.has(listId) && selected.size >= MAX_GROUPS) {
      setError(`Você pode selecionar no máximo ${MAX_GROUPS} grupos.`);
      return;
    }
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(listId)) next.delete(listId);
      else next.add(listId);
      return next;
    });
  };

  const selectAllFiltered = () => {
    setError(null);
    const ids = filtered.map((g) => g.id).filter((id) => id > 0);
    if (ids.length > MAX_GROUPS) {
      setError(`Selecione no máximo ${MAX_GROUPS} grupos (a lista filtrada tem ${ids.length}).`);
      setSelected(new Set(ids.slice(0, MAX_GROUPS)));
      return;
    }
    setSelected(new Set(ids));
  };

  const clearSelection = () => setSelected(new Set());

  const handleExport = async () => {
    if (selected.size === 0) {
      setError('Selecione pelo menos um grupo');
      return;
    }
    setExporting(true);
    setError(null);
    try {
      const apiIds: number[] = [];
      const seen = new Set<number>();
      for (const listId of Array.from(selected)) {
        const group = groups.find((g) => g.id === listId);
        const apiId = resolveWhatsAppGroupApiId(group ?? null, listId);
        if (apiId != null && apiId > 0 && !seen.has(apiId)) {
          seen.add(apiId);
          apiIds.push(apiId);
        }
      }
      if (apiIds.length === 0) {
        setError('Não foi possível identificar os grupos para exportar');
        return;
      }
      const info = await exportWhatsAppGroupParticipants(sectorId, apiIds);
      onExported?.(info);
      onClose();
    } catch (err: unknown) {
      setError(resolveGroupParticipantsErrorMessage(err, 'Não foi possível exportar os contatos'));
    } finally {
      setExporting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="broadcast-modal-overlay"
      onClick={() => !exporting && onClose()}
      role="presentation"
    >
      <div
        className="broadcast-modal export-group-contacts-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="export-group-contacts-title"
      >
        <div className="broadcast-modal-header">
          <h2 id="export-group-contacts-title">Exportar contatos de grupos</h2>
          <button
            type="button"
            className="broadcast-modal-close"
            onClick={onClose}
            disabled={exporting}
            aria-label="Fechar"
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="broadcast-modal-body">
          <p className="export-group-contacts-modal__hint">
            Selecione grupos do WhatsApp desta conexão. Os membros são reunidos em uma única planilha
            (colunas Name e Phone Number), no mesmo formato usado para importar contatos em campanhas.
          </p>

          <div className="export-group-contacts-modal__toolbar">
            <input
              type="text"
              className="form-input"
              placeholder="Buscar grupos…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={loading || exporting}
            />
            <div className="export-group-contacts-modal__toolbar-actions">
              <button type="button" className="btn-secondary btn-sm" onClick={selectAllFiltered} disabled={loading || exporting || filtered.length === 0}>
                Selecionar todos
              </button>
              <button type="button" className="btn-secondary btn-sm" onClick={clearSelection} disabled={exporting || selected.size === 0}>
                Limpar
              </button>
              <span className="export-group-contacts-modal__count">
                {selected.size} selecionados
              </span>
            </div>
          </div>

          {error && (
            <div className="export-group-contacts-modal__error" role="alert">
              {error}
            </div>
          )}

          <div className="export-group-contacts-modal__list">
            {loading ? (
              <div className="export-group-contacts-modal__empty">
                <LoadingSpinner size="md" />
                <span>Carregando grupos…</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="export-group-contacts-modal__empty">
                {groups.length === 0
                  ? 'Nenhum grupo do WhatsApp encontrado. Sincronize os grupos em Conexões primeiro.'
                  : 'Nenhum grupo corresponde à sua busca.'}
              </div>
            ) : (
              <Virtuoso
                style={{ height: 320 }}
                data={filtered}
                itemContent={(_i, g) => {
                  const checked = selected.has(g.id);
                  const members = g.participantCount ?? 0;
                  return (
                    <label className="export-group-contacts-modal__row">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={exporting}
                        onChange={() => toggle(g.id)}
                      />
                      <span className="export-group-contacts-modal__row-name">{g.name || `Grupo #${g.id}`}</span>
                      <span className="export-group-contacts-modal__row-meta">
                        {members > 0 ? `${members} membros` : '—'}
                      </span>
                    </label>
                  );
                }}
              />
            )}
          </div>
        </div>

        <div className="broadcast-modal-footer">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={exporting}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => void handleExport()}
            disabled={exporting || selected.size === 0}
          >
            {exporting ? (
              <>
                <LoadingSpinner size="sm" /> Exportando…
              </>
            ) : (
              <>
                <FiDownload size={14} /> Baixar Excel
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
